import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { APICommonService } from '@nationwide/internet-servicing-angular-services';
import { commercialConstants } from 'app/common/commercial-constants';
import { environment as globalEnvironment } from 'environments/environment';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, publishReplay, refCount } from 'rxjs/operators';
import { AccountRetrievalService } from './account-retrieval.service';

// import { RetrieveBillsService } from './retrieve-bills.service';
import { TokenDataService } from 'app/shared/services/token-data.service';

import { CommercialConsumerData, environmentDefaultConfig } from 'app/commercial/models/package-data';
import { ClaimsDataService } from './claims-data.service';

@Injectable()
export class PackageService {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _coiData: any;
    env: any;
    packagesData: any;
    constructor(
        @Optional() @Inject('config') private environment: any = environmentDefaultConfig,
        private apiCommonService: APICommonService,
        private httpClient: HttpClient,
        private paymentRetrieve: AccountRetrievalService,
        private tokenDataService: TokenDataService,
        private claimsDataService: ClaimsDataService
    ) { }

    private getBillingEnvironmentConfig(): any {
        return this.resolveBillingEnvironment(this.environment || environmentDefaultConfig);
    }

    private getAppClientId(): string {
        return globalEnvironment?.appProps?.appClientId || '';
    }

    private resolveBillingEnvironment(envConfig: any): any {
        const endpoint = envConfig?.EXPERIENCE_API?.ENDPOINT ||
            envConfig?.commonServiceAPIs?.billingExperience ||
            environmentDefaultConfig.EXPERIENCE_API.ENDPOINT;

        return {
            ...environmentDefaultConfig,
            ...envConfig,
            EXPERIENCE_API: {
                ...environmentDefaultConfig.EXPERIENCE_API,
                ...(envConfig?.EXPERIENCE_API || {}),
                ENDPOINT: endpoint
            },
            BILLING: {
                ...environmentDefaultConfig.BILLING,
                ...(envConfig?.BILLING || {})
            }
        };
    }

    private getCookieByName(name: string): string | null {
        const pair = document.cookie.match(new RegExp(`${name}=([^;]+)`));
        return pair ? pair[1] : null;
    }

    private resolveTargetEnvironment(billingEnvironment: any): string | null {
        const targetEnvironment = sessionStorage.getItem('targetEnvironment');
        if (targetEnvironment) {
            return targetEnvironment;
        }

        const claimsCookies = this.environment?.claimsCookies || {};
        const billingFlexCookie = claimsCookies.billingFlexRegion || 'billingFlexRegion';
        const billingBcCookie = claimsCookies.billingBcRegion || 'billingBcRegion';

        const billingFlexRegion = this.getCookieByName(billingFlexCookie) || billingEnvironment.BILLING?.FLEX_REGION;
        const billingBcRegion = this.getCookieByName(billingBcCookie) || billingEnvironment.BILLING?.BC_REGION;

        if (billingFlexRegion && billingBcRegion) {
            return `${billingFlexRegion}|${billingBcRegion}`;
        }

        return billingFlexRegion || null;
    }

    callCOIEligibilityAPI(): Observable<any> {
        if (!this._coiData) {
            const billingEnvironment = this.getBillingEnvironmentConfig();
            const retrieveCoiEligibilityUrl = `${billingEnvironment.EXPERIENCE_API.ENDPOINT}coi-eligibility`;
            const authToken = this.tokenDataService.getToken();
            const appClientId = this.getAppClientId();
            const headers = this.apiCommonService.buildHeader({
                appClientId,
                authToken,
                contentType: 'text/plain',
                headerType: 'X-NW-MessageID'
            });

            this._coiData = this.httpClient.get(retrieveCoiEligibilityUrl, { headers })
                .pipe(map((response: any) =>
                    response),
                    publishReplay(1),
                    refCount());
        }

        return this._coiData;
    }

    clearCache(): void {
        this.packagesData = undefined;
    }

    retrieveBillingInformation(uniqueIdentifier?: string): any {
        return this.callPackageAPI(this.createConsumerData(uniqueIdentifier)).pipe(mergeMap(
            (packagesResponse) => this.billingInformationForkJoin(packagesResponse)
        ));
    }

    retrieveCOIEligibilityData(): any {
        return this.callCOIEligibilityAPI().pipe(map((data) => data));
    }

    retrievePackageDataForFarmFlex(uniqueIdentifier?: string): Observable<any> {
        return this.callPackageAPI(this.createConsumerData(uniqueIdentifier)).pipe(map((packagesResponse) => {
            for (const account of packagesResponse) {
                if (this.isMatchingFarmFlexAccount(account, uniqueIdentifier)) {
                    this.setMatchedFarmFlexPackage(account);
                    const effectiveDateMap = this.buildEffectiveDateMap(account.packages);
                    return this.mapRelatedPolicies(account.relatedPolicies, effectiveDateMap);
                }
            }
            return [];
        }), catchError((error) =>
            throwError(() => new Error(error))
        ));
    }

    retrievePackageDataforClaims(uniqueIdentifier?: string): Observable<any> {
        return this.callPackageAPI(this.createConsumerData(uniqueIdentifier)).pipe(map((packagesResponse) => {
            let currentpak = {};
            const accounts = packagesResponse;
            for (const account of accounts) {
                for (const pak of account.packages) {
                    if (pak.policyAccount) {
                        const policyAccount = pak.policyAccount;
                        const policyLength = policyAccount.policyNumber.length;
                        const inputPolicy = uniqueIdentifier?.slice(uniqueIdentifier?.length - policyLength);
                        if (policyAccount.policyNumber === inputPolicy) {
                            currentpak = pak;
                            break;
                        }
                    }
                }
            }
            return currentpak;
        }), catchError((error) =>
            throwError(() => new Error(error))));
    }

    private billingInformationForkJoin(packagesResponse: any[]): any {
        const billingDataArray = [];
        if (packagesResponse.length > 0) {
            for (const index in packagesResponse) {
                if (packagesResponse[index].accountNumber) {
                    // TODO: This is tech debt.  We should instead handle this through our experience API
                    if (packagesResponse[index].sourceSystem === commercialConstants.billingCenterSourceSystem) {
                        billingDataArray.push(
                            this.paymentRetrieve.retrieveAccountData(packagesResponse[index].accountNumber));
                    } else {
                        // billingDataArray.push(this.retrieveBillsService.retrieveBills(packagesResponse[index].accountNumber));
                    }
                }
            }
        }
        if (billingDataArray.length === 0) {
            return of([]);
        }
        return forkJoin(billingDataArray);
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
    private buildEffectiveDateMap(packages: any[]): Map<string, string> {
        const effectiveDateMap = new Map<string, string>();
        for (const policyPackage of packages ?? []) {
            if (policyPackage.policyAccount) {
                const fullNumber = (policyPackage.policyAccount.branchNumber ?? '') + (policyPackage.policyAccount.decNumber ?? '') + (policyPackage.policyAccount.policyNumber ?? '');
                if (fullNumber && policyPackage.policyAccount.effectiveDate) {
                    effectiveDateMap.set(fullNumber, policyPackage.policyAccount.effectiveDate);
                }
            }
            for (const policy of policyPackage.policies ?? []) {
                if (policy.policyNumber && policy.effectiveDate) {
                    effectiveDateMap.set(policy.policyNumber, policy.effectiveDate);
                }
            }
        }
        return effectiveDateMap;
    }

    private callPackageAPI(consumerData: CommercialConsumerData): Observable<any> {
        if (!this.packagesData) {
            const billingEnvironment = this.resolveBillingEnvironment(consumerData['environment'] || this.getBillingEnvironmentConfig());
            this.env = billingEnvironment;

            const retrievePackageUrl = `${billingEnvironment.EXPERIENCE_API.ENDPOINT}packages`;

            const authToken = consumerData.authToken;
            const appClientId = consumerData.clientId;
            let headers = this.apiCommonService.buildHeader({ appClientId, authToken, contentType: 'text/plain', headerType: 'X-NW-MessageID' });

            const validatorUrl = `${billingEnvironment.validatorUrl}${consumerData.uniqueIdentifier}/retrieve`;
            const packageRequestHeaders = new HttpHeaders({
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'X-NW-Message-ID': this.apiCommonService.generateTransactionId(),
                client_id: appClientId
            });

            const region = this.resolveTargetEnvironment(billingEnvironment);
            if (region) {
                headers = headers.append('X-NW-Target-Env', region);
            }
            // headers = headers.append('x-override-packages-env', 'test');
            if (billingEnvironment.useValidatorService && sessionStorage.getItem('useValidator') === 'true') {
                const validatorParameters = {
                    serviceName: 'CommercialLinesBillingExperience_1',
                    applicationName: billingEnvironment.applicationName,
                    endpoint: 'https://api-test.nwie.net/payment-management/commercial-lines-billing/v1/packages'
                };
                return this.httpClient.post(validatorUrl, validatorParameters, { headers: packageRequestHeaders })
                    .pipe(map((response: any) =>
                        this.parseResponse(JSON.parse(response['cachedResponses'][0]['payload']))),
                        publishReplay(1),
                        refCount());
            } else {
                return this.packagesData = this.httpClient.get(retrievePackageUrl, { headers })
                    .pipe(map((response) => this.parseResponse(response)),
                        publishReplay(1),
                        refCount());
            }
        }
        return this.packagesData;
    }

    private createConsumerData(pakIdentifier?: string): any {
        const envConfig = this.getBillingEnvironmentConfig();
        const appClientId = this.getAppClientId();

        if (pakIdentifier) {
            const consumerData: CommercialConsumerData = {
                clientId: appClientId,
                authToken: this.tokenDataService.getToken(),
                environment: envConfig,
                uniqueIdentifier: pakIdentifier
            };
            return consumerData;
        } else {
            const consumerData: CommercialConsumerData = {
                clientId: appClientId,
                authToken: this.tokenDataService.getToken(),
                environment: envConfig
            };
            return consumerData;
        }
    }

    private equivalentPaks(relatedPak: string, policyAccount: any): boolean {
        const decDigitIndex = 2;
        return relatedPak.slice(0, decDigitIndex) + relatedPak.slice(decDigitIndex + 1, relatedPak.length) === policyAccount.branchNumber + policyAccount.policyNumber;
    }

    private isMatchingFarmFlexAccount(account: any, uniqueIdentifier?: string): boolean {
        const pakSuffixLength = 7;
        const normalizedPak = (account.pakNumber ?? '').replace(/\D/g, '').slice(-pakSuffixLength);
        const normalizedId = (uniqueIdentifier ?? '').replace(/\D/g, '').slice(-pakSuffixLength);
        return Boolean(normalizedPak) && normalizedPak === normalizedId;
    }

    private setMatchedFarmFlexPackage(account: any): void {
        const matchedPackage = account.packages?.[0];
        if (matchedPackage) {
            this.claimsDataService.setClaimsPackage(matchedPackage);
        }
    }

    private mapRelatedPolicies(relatedPolicies: any[], effectiveDateMap = new Map<string, string>()): any[] {
        if (!Array.isArray(relatedPolicies) || relatedPolicies.length === 0) {
            return [];
        }

        // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
        return relatedPolicies.map((policy) => {
            const [expirationMonth, expirationDay, expirationYear] = (policy.policyExpirationDate ?? '').split('/');
            const expirationDate = expirationYear ? `${expirationYear}-${expirationMonth}-${expirationDay}` : '';

            const knownEffectiveDate = effectiveDateMap.get(policy.policyNumber ?? '');
            let effectiveDate: string;
            if (knownEffectiveDate) {
                effectiveDate = knownEffectiveDate;
            } else if (expirationDate) {
                const [year, month, day] = expirationDate.split('-').map((value) => Number(value));
                effectiveDate = `${year - 1}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            } else {
                effectiveDate = '';
            }

            return {
                prefixA: policy.packagePrefix ?? '',
                prefixB: policy.policyPrefix ?? '',
                policyNumber: policy.policyNumber ?? '',
                effectiveDate,
                expirationDate
            };
        });
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
    private parseResponse(responseJson: any): any {
        const restructuredArray = [];
        if (responseJson.billingAccounts.length > 0) {
            for (const billingIndex in responseJson.billingAccounts) {
                if (responseJson.billingAccounts[billingIndex].account) {
                    const billingAccount = responseJson.billingAccounts[billingIndex].account;
                    billingAccount.packages = [];
                    billingAccount.fullBills = [];
                    let relatedPaks: string[] = [];
                    for (const k in billingAccount.relatedPolicies) {
                        if (billingAccount.relatedPolicies[k].policyNumber !== '0000000000' && !relatedPaks.includes(billingAccount.relatedPolicies[k].policyNumber)) {
                            relatedPaks.push(billingAccount.relatedPolicies[k].policyNumber);
                        }
                    }
                    relatedPaks = relatedPaks.filter((pak, index) => relatedPaks.indexOf(pak) === index);
                    for (const l in responseJson.policyPackages) {
                        if (responseJson.policyPackages[l].policyAccount) {
                            const policy = responseJson.policyPackages[l];
                            for (const pakIndex in relatedPaks) {
                                if (this.equivalentPaks(relatedPaks[pakIndex], policy.policyAccount) && !billingAccount.packages.includes(responseJson.policyPackages[l])) {
                                    billingAccount.packages.push(responseJson.policyPackages[l]);
                                }
                            }
                        }
                    }
                    restructuredArray.push(billingAccount);
                }
            }
        }
        return restructuredArray;
    }
}
